import React from "react";
import { Helmet } from "react-helmet-async";
import { MdLayers, MdDelete, MdRemove, MdAdd } from "react-icons/md";

import styles from "./ComplexCart.module.scss";
import { useComplexCart } from "./useComplexCart";
import { PageHeader } from "../../common/components/pageHeader/PageHeader";
import { EmptyState } from "../../common/components/emptyState/EmptyState";
import { getClusterImageUrl } from "../../common/utils/functions/getImageUri";
import { ItemAttribute } from "../../common/types/interfaces";

export const ComplexCart: React.FC = () => {
  const {
    grouped,
    selectedClusterId,
    setSelectedClusterId,
    selectedGroup,
    groupedItems,
    fieldsByCategory,
    isEmpty,
    increase,
    decrease,
    remove,
    removeAll,
  } = useComplexCart();

  if (isEmpty) {
    return (
      <div className={styles.Page}>
        <Helmet>
          <title>Проект — ЛОГМОЛЛ</title>
        </Helmet>
        <PageHeader title="Проект" />
        <EmptyState
          icon={MdLayers}
          title="Проект пуст"
          description="Добавьте товары в проект, чтобы оформить заявку."
        />
      </div>
    );
  }

  const { parents, childrenMap } = groupedItems;

  const renderAttributes = (attrs?: ItemAttribute[]) => {
    if (!attrs?.length) return null;
    const visible = attrs.filter((a) => a.isVisible);
    if (!visible.length) return null;
    return (
      <div className={styles.AttributesList}>
        {visible.map((a) => (
          <span key={String(a.id)} className={styles.AttributeItem}>
            {a.name}: {String(a.value)}
          </span>
        ))}
      </div>
    );
  };

  const renderParent = (parent: any) => {
    const children: any[] = childrenMap[parent.id] || [];
    const hasChildren = children.length > 0;
    const isComplex = parent.category?.isComplex ?? false;
    const isFixedPrice = isComplex && parent.price > 0;
    const cluster = selectedGroup?.cluster;

    const parentImage =
      parent.image && cluster
        ? getClusterImageUrl(cluster, parent.image)
        : null;

    const childrenTotal = children.reduce((s, c) => s + (c.total || 0), 0);

    let displayPrice = 0;
    if (isFixedPrice) {
      displayPrice = parent.price;
    } else if (isComplex) {
      displayPrice = childrenTotal || parent.total;
    } else {
      displayPrice = parent.total;
    }

    return (
      <div key={parent.id} className={styles.Group}>
        <div className={styles.ItemCard}>
          {parentImage ? (
            <img
              src={parentImage}
              alt={parent.name}
              className={styles.ItemImage}
            />
          ) : (
            <div className={styles.ItemImagePlaceholder} />
          )}

          <div className={styles.ItemInfo}>
            <span className={styles.ItemName}>{parent.name}</span>
            {renderAttributes(parent.attributes)}
            {displayPrice > 0 && (
              <span className={styles.ItemPrice}>{displayPrice} ₽</span>
            )}
          </div>

          <div className={styles.ItemActions}>
            <button
              className={styles.IconButton}
              onClick={() => decrease(parent.id)}
              aria-label="Уменьшить"
            >
              <MdRemove size={22} />
            </button>
            <span className={styles.QuantityText}>{parent.quantity}</span>
            <button
              className={styles.IconButton}
              onClick={() => increase(parent.id)}
              aria-label="Увеличить"
            >
              <MdAdd size={22} />
            </button>
            <button
              className={styles.IconButton}
              onClick={() =>
                hasChildren ? removeAll(parent.id) : remove(parent.id)
              }
              aria-label="Удалить"
            >
              <MdDelete size={22} />
            </button>
          </div>
        </div>

        {hasChildren && (
          <div className={styles.Children}>
            {children.map((child: any) => {
              const childImage =
                child.image && cluster
                  ? getClusterImageUrl(cluster, child.image)
                  : null;

              return (
                <div key={child.id} className={styles.ChildRow}>
                  {childImage ? (
                    <img
                      src={childImage}
                      alt={child.name}
                      className={styles.ChildImage}
                    />
                  ) : (
                    <div className={styles.ChildImagePlaceholder} />
                  )}
                  <span className={styles.ChildName}>{child.name}</span>
                  <span className={styles.ChildQty}>× {child.quantity}</span>

                  {!isFixedPrice && (
                    <>
                      <button
                        className={styles.IconButton}
                        onClick={() => decrease(child.id)}
                        aria-label="Уменьшить"
                      >
                        <MdRemove size={18} />
                      </button>
                      <button
                        className={styles.IconButton}
                        onClick={() => increase(child.id)}
                        aria-label="Увеличить"
                      >
                        <MdAdd size={18} />
                      </button>
                      <button
                        className={styles.IconButton}
                        onClick={() => remove(child.id)}
                        aria-label="Удалить"
                      >
                        <MdDelete size={18} />
                      </button>
                    </>
                  )}

                  {!isFixedPrice && child.price > 0 && (
                    <span className={styles.ChildPrice}>{child.price} ₽</span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={styles.Page}>
      <Helmet>
        <title>Проект — ЛОГМОЛЛ</title>
      </Helmet>

      <PageHeader title="Проект" />

      <div className={styles.Layout}>
        <aside className={styles.ClusterList}>
          {grouped.map((group) => {
            const isActive = selectedClusterId === group.clusterId;
            const cluster = group.cluster;
            return (
              <button
                key={group.clusterId}
                type="button"
                className={styles.ClusterCard}
                data-active={isActive || undefined}
                onClick={() => setSelectedClusterId(group.clusterId)}
              >
                {cluster && (
                  <img
                    src={`https://${cluster.domain}/icons/logo512.png`}
                    alt={cluster.name}
                    className={styles.ClusterLogo}
                  />
                )}
                <div className={styles.ClusterInfo}>
                  <span className={styles.ClusterName}>
                    {cluster?.name || `Кластер #${group.clusterId}`}
                  </span>
                  <span className={styles.ClusterMeta}>
                    {group.items.length} поз.
                  </span>
                </div>
              </button>
            );
          })}
        </aside>

        <section className={styles.ItemsSection}>
          {!selectedGroup ? (
            <EmptyState
              icon={MdLayers}
              title="Выберите компанию"
              description="Выберите компанию слева, чтобы увидеть проект."
            />
          ) : (
            <>
              <h2 className={styles.SectionTitle}>
                {selectedGroup.cluster?.name}
              </h2>

              <div className={styles.ItemsList}>
                {parents.map((parent: any) => renderParent(parent))}
              </div>

              {fieldsByCategory.length > 0 && (
                <div className={styles.FieldsBlock}>
                  <h3 className={styles.FieldsTitle}>Детали проекта</h3>
                  {fieldsByCategory.map((group) => (
                    <div key={group.categoryId} className={styles.FieldsGroup}>
                      <span className={styles.FieldsCategory}>
                        {group.categoryName}
                      </span>
                      <div className={styles.FieldsList}>
                        {group.fields.map((f: any) => (
                          <div key={f.id} className={styles.FieldRow}>
                            <span className={styles.FieldLabel}>
                              {f.name}
                              {f.required && (
                                <span className={styles.Required}> *</span>
                              )}
                            </span>
                            <input
                              className={styles.FieldInput}
                              type={f.type === 1 ? "number" : "text"}
                              placeholder={f.name}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <button
                type="button"
                className={styles.CheckoutButton}
                onClick={() => {
                  alert("Отправка заявки появится позже");
                }}
              >
                <span className={styles.CheckoutBg} aria-hidden="true">
                  Отправить
                </span>
                <span className={styles.CheckoutLabel}>Отправить</span>
              </button>
            </>
          )}
        </section>
      </div>
    </div>
  );
};